import { HoldToken } from '@seatsio/seatsio-react';
import { createStore, Mutate, StoreApi } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';

import { hasWindow } from 'utils/browser.utils';
import { isLocal } from 'utils/env.utils';

import {
  EventKey,
  LocalCartItem,
  SeatingInfo,
  SeatingToken,
} from 'types/tickets.types';

type NofomoCartStoreState = {
  eventKey: EventKey;
  items: Array<LocalCartItem>;
  ticketingCartId: string | undefined;
  seatingToken: SeatingToken | undefined;
  seatingInfo: SeatingInfo | undefined;
  ticketOffer:
    | {
        code: string;
        customerLoyaltyId?: string;
      }
    | undefined;
  ticketingActiveOrderId: string | undefined;
};

type CartItem = Pick<LocalCartItem, 'catalogueItemId' | 'name' | 'quantity'>;

type SeatedCartItem = Required<
  Pick<LocalCartItem, 'catalogueItemId' | 'name' | 'bookObjectsInfo'>
>;

type NofomoCartStoreActions = {
  setSeatingToken: (holdToken: Pick<HoldToken, 'token' | 'expiresAt'>) => void;
  setSeatingTokenExpired: () => void;
  removeCartSeatedItems: () => void;
  removeTicketingCartAndSeats: () => void;
  setSeatedItemToCart: (items: SeatedCartItem) => void;
  removeSeatedItemFromCart: (item: {
    catalogueItemId: string;
    bookObject: string;
  }) => void;
  setSeatingInfo: (seatingInfo: SeatingInfo) => void;
  setTicketingCartId: (cartId: string) => void;
  removeSeatingInfo: () => void;
  addItemToCart: (item: CartItem) => void;
  removeItemFromCart: (
    item: Pick<CartItem, 'catalogueItemId' | 'quantity'>,
  ) => void;
  removeLocalItemFromCart: (item: LocalCartItem) => void;
  setTicketOffer: (offer: NofomoCartStoreState['ticketOffer']) => void;
  setTicketingActiveOrderId: (orderId: string | undefined) => void;
  resetStore: () => void;
};

export type NofomoCartStore = NofomoCartStoreState & {
  actions: NofomoCartStoreActions;
};

export const initNofomoCartStore = ({
  eventKey,
  ...state
}: {
  eventKey: string;
} & Partial<NofomoCartStoreState>): NofomoCartStoreState => ({
  eventKey,
  items: [],
  seatingToken: undefined,
  seatingInfo: undefined,
  ticketingCartId: undefined,
  ticketOffer: undefined,
  ticketingActiveOrderId: undefined,
  ...state,
});

export type NofomoCartStoreReturnType = ReturnType<typeof createCartStore>;

type LocalStorageState = Pick<
  NofomoCartStoreState,
  | 'items'
  | 'seatingToken'
  | 'ticketingCartId'
  | 'ticketingActiveOrderId'
  | 'ticketOffer'
>;

type StoreWithPersist = Mutate<
  StoreApi<NofomoCartStore>,
  [['zustand/immer', unknown], ['zustand/persist', LocalStorageState]]
>;

const withStorageDOMEvents = (store: StoreWithPersist) => {
  const storageEventCallback = (e: StorageEvent) => {
    if (e?.key === store?.persist?.getOptions?.()?.name && e?.newValue) {
      store.persist.rehydrate();
    }
  };

  if (hasWindow()) {
    window.addEventListener('storage', storageEventCallback);

    return () => {
      window.removeEventListener('storage', storageEventCallback);
    };
  }
};

export const createCartStore = (initState: NofomoCartStoreState) => {
  const store = createStore<NofomoCartStore>()(
    devtools(
      immer(
        persist(
          (set) => ({
            ...initState,
            actions: {
              setSeatingToken: (holdToken) => {
                set(
                  (state) => {
                    state.seatingToken = {
                      ...holdToken,
                      expired: false,
                      expirationHandled: false,
                    };
                  },
                  false,
                  { type: 'setSeatingToken' },
                );
              },
              setSeatingTokenExpired: () => {
                set(
                  (state) => {
                    if (state.seatingToken) {
                      state.seatingToken.expired = true;
                    }
                  },
                  false,
                  { type: 'setSeatingTokenExpired' },
                );
              },
              removeCartSeatedItems: () => {
                set(
                  (state) => {
                    // Remove all items with bookObjectsInfo
                    state.items = state.items.filter(
                      (item) => !item.bookObjectsInfo?.length,
                    );
                  },
                  false,
                  { type: 'removeCartSeatedItems' },
                );
              },
              removeTicketingCartAndSeats: () => {
                set(
                  (state) => {
                    if (state.seatingToken) {
                      state.seatingToken.expirationHandled = true;
                    }
                    // Also clear the stored cartId, we create a new one
                    if (state.ticketingCartId) {
                      state.ticketingCartId = undefined;
                    }
                    // Remove all items with bookObjectsInfo
                    state.items = state.items.filter(
                      (item) => !item.bookObjectsInfo?.length,
                    );
                  },
                  false,
                  { type: 'removeTicketingCartAndSeats' },
                );
              },

              setSeatingInfo: (seatingInfo) => {
                set(
                  (state) => {
                    state.seatingInfo = seatingInfo;
                  },
                  false,
                  { type: 'setSeatingInfo' },
                );
              },
              removeSeatingInfo: () => {
                set(
                  (state) => {
                    state.seatingInfo = undefined;
                  },
                  false,
                  { type: 'removeSeatingInfo' },
                );
              },

              // Deals with seated items, they always "replace" any existing matching items
              setSeatedItemToCart: (item) => {
                set(
                  (state) => {
                    const existingItem = state.items.find(
                      (i) => i.catalogueItemId === item.catalogueItemId,
                    );

                    if (!existingItem) {
                      state.items.push({
                        ...item,
                        quantity: item.bookObjectsInfo.length,
                      });
                    } else {
                      // Filter and merge new bookObjectsInfo ensuring uniqueness
                      const newBookObjectsInfo = item.bookObjectsInfo.filter(
                        (info) =>
                          !existingItem.bookObjectsInfo?.some(
                            (existingInfo) => existingInfo.label === info.label,
                          ),
                      );

                      existingItem.bookObjectsInfo = [
                        ...(existingItem.bookObjectsInfo ?? []),
                        ...newBookObjectsInfo,
                      ];

                      existingItem.quantity =
                        existingItem.bookObjectsInfo.length;
                    }
                  },
                  false,
                  { type: 'setSeatedItemToCart' },
                );
              },

              removeSeatedItemFromCart: (item) => {
                set((state) => {
                  const existingItem = state.items.find(
                    (i) => i.catalogueItemId === item.catalogueItemId,
                  );

                  if (existingItem) {
                    existingItem.bookObjectsInfo =
                      existingItem.bookObjectsInfo?.filter(
                        (info) => info.label !== item.bookObject,
                      );

                    existingItem.quantity =
                      existingItem.bookObjectsInfo?.length ?? 0;

                    if (existingItem.quantity === 0) {
                      state.items = state.items.filter(
                        (i) => i.catalogueItemId !== item.catalogueItemId,
                      );
                    }
                  }
                });
              },
              // Increase the quantity of an item in the cart
              addItemToCart: (item) => {
                set(
                  (state) => {
                    const index = state.items.findIndex(
                      (i) => i.catalogueItemId === item.catalogueItemId,
                    );

                    if (index === -1) {
                      state.items.push({
                        ...item,
                        bookObjectsInfo: [],
                      });
                    } else {
                      state.items[index].quantity += item.quantity;
                    }
                  },
                  false,
                  { type: 'addItemToCart' },
                );
              },
              removeItemFromCart: (item) => {
                set(
                  (state) => {
                    state.items = state.items.reduce<Array<LocalCartItem>>(
                      (acc, currentItem) => {
                        if (
                          currentItem.catalogueItemId !== item.catalogueItemId
                        ) {
                          acc.push(currentItem);
                          return acc;
                        }
                        const newQuantity =
                          currentItem.quantity - item.quantity;
                        if (newQuantity > 0) {
                          acc.push({ ...currentItem, quantity: newQuantity });
                        }
                        return acc;
                      },
                      [],
                    );
                  },
                  false,
                  { type: 'removeItemFromCart' },
                );
              },
              removeLocalItemFromCart: (item) => {
                set(
                  (state) => {
                    state.items = state.items.filter(
                      (i) => i.catalogueItemId !== item.catalogueItemId,
                    );
                  },
                  false,
                  { type: 'removeLocalItemFromCart' },
                );
              },
              setTicketingCartId: (cartId) => {
                set(
                  (state) => {
                    state.ticketingCartId = cartId;
                  },
                  false,
                  { type: 'setTicketingCartId' },
                );
              },
              setTicketOffer: (params: NofomoCartStoreState['ticketOffer']) => {
                set(
                  (state) => {
                    state.ticketOffer = params;
                  },
                  false,
                  { type: 'setTicketOffer' },
                );
              },
              setTicketingActiveOrderId: (orderId) => {
                set(
                  (state) => {
                    state.ticketingActiveOrderId = orderId;
                  },
                  false,
                  { type: 'setTicketingActiveOrderId' },
                );
              },
              resetStore: () => {
                set(
                  (state) => {
                    state.items = [];
                    state.seatingToken = undefined;
                    state.ticketingCartId = undefined;
                    state.ticketOffer = undefined;
                    state.ticketingActiveOrderId = undefined;
                  },
                  false,
                  { type: 'resetStore' },
                );
              },
            },
          }),

          {
            // Funny name for nofomo-store
            name: `nf-cart-${initState.eventKey}`,
            partialize: (state): LocalStorageState => ({
              items: state.items,
              seatingToken: state.seatingToken,
              ticketingCartId: state.ticketingCartId,
              ticketingActiveOrderId: state.ticketingActiveOrderId,
              ticketOffer: state.ticketOffer,
            }),
          },
        ),
      ),
      { name: 'nofomo-cart', store: 'nf-cart', enabled: isLocal },
    ),
  );
  // Sync the store across tabs
  withStorageDOMEvents(store);
  return store;
};
